import { isWeekend, parseISO, format } from 'date-fns';

export const FULL_DAY_HOURS    = 7.5;
export const HALF_DAY_AM_HOURS = 3;
export const HALF_DAY_PM_HOURS = 4.5;

export function parseWorkDays(sched) {
  if (!sched) return null;
  let wd = sched.days ?? sched.workDays ?? sched.work_days;
  if (wd === undefined || wd === null) return null;
  if (typeof wd === 'string') {
    try { wd = JSON.parse(wd); } catch { return null; }
  }
  if (!Array.isArray(wd) || wd.length < 7) return null;
  return wd.map(v => v === true || v === 1 || v === '1' || v === 'true');
}

export function getActiveSchedule(empSchedules, day) {
  if (!empSchedules?.length) return null;
  const dateStr = format(day, 'yyyy-MM-dd');
  const getFrom = s =>
    (s.effectiveFrom ?? s.effective_from ?? s.from ?? '1970-01-01').slice(0, 10);
  const past = empSchedules.filter(s => getFrom(s) <= dateStr);
  if (past.length) return past.sort((a, b) => getFrom(b).localeCompare(getFrom(a)))[0];
  return [...empSchedules].sort((a, b) => getFrom(a).localeCompare(getFrom(b)))[0];
}

export function isNonWorkingDay(empSchedules, day) {
  if (!empSchedules?.length) return false;
  const sched    = getActiveSchedule(empSchedules, day);
  const workDays = parseWorkDays(sched);
  if (!workDays) return false;
  const jsDay    = day.getDay();
  const schedIdx = jsDay === 0 ? 6 : jsDay - 1;
  return !workDays[schedIdx];
}

export function getHoursPerDay(empSchedules, day) {
  const sched = getActiveSchedule(empSchedules, day);
  return parseFloat(sched?.hoursPerDay ?? sched?.hours_per_day ?? 7.5);
}

export function isHalfDayLeave(leave) {
  if (!leave) return false;
  if (leave.halfDay === true || leave.isHalfDay === true) return true;
  if (leave.halfDayPeriod && leave.halfDayPeriod !== null) return true;
  if (leave.days !== undefined && parseFloat(leave.days) === 0.5) return true;
  return false;
}

export function getHalfDayPeriod(leave) {
  const period = leave?.halfDayPeriod ?? leave?.period ?? null;
  if (!period) return null;
  const p = String(period).toLowerCase();
  if (p === 'am' || p === 'morning') return 'AM';
  if (p === 'pm' || p === 'afternoon') return 'PM';
  return null;
}

export function calcAllowance(emp, requests, allowancesMap, schedulesMap, leaveColorsMap = {}) {
  const rec         = allowancesMap?.[emp.id];

  // Server-calculated balance (leave engine), when the allowance response has one
  const server = rec?.balance;
  if (server && typeof server === 'object' && !server.noBalanceTracking) {
    const fmtHrs = h => { const v = Math.round((Number(h) || 0) * 10) / 10; return `${v}h`; };
    const fmtDys = d => {
      const v = Number(d) || 0;
      const whole = Math.floor(Math.abs(v));
      const hasHalf = Math.abs((Math.abs(v) - whole) - 0.5) < 0.01;
      const sign = v < 0 ? '-' : '';
      if (hasHalf) return whole > 0 ? `${sign}${whole}½d` : `${sign}½d`;
      return `${sign}${Math.round(Math.abs(v) * 10) / 10}d`;
    };
    if (server.unit === 'hours') {
      return {
        remainingLabel: fmtHrs(server.available?.hours),
        pendingLabel:   server.pending?.hours > 0 ? fmtHrs(server.pending.hours) : null,
        usedHours:      server.used?.hours ?? 0,
        totalHours:     server.total?.hours ?? 0,
        remainingHours: server.available?.hours ?? 0,
      };
    }
    return {
      remainingLabel: fmtDys(server.available?.days),
      pendingLabel:   server.pending?.days > 0 ? fmtDys(server.pending.days) : null,
      usedDays:       server.used?.days ?? 0,
      totalDays:      server.total?.days ?? 0,
      remainingDays:  server.available?.days ?? 0,
    };
  }

  const displayUnit = rec?.displayUnit ?? rec?.display_unit ?? 'days';
  const empScheds   = schedulesMap?.[emp.id] ?? [];
  const defaultHpd  = empScheds.length ? getHoursPerDay(empScheds, new Date()) : FULL_DAY_HOURS;

  const totalHours =
    parseFloat(rec?.allowanceHours    ?? rec?.allowance_hours    ?? 116) +
    parseFloat(rec?.carryForwardHours ?? rec?.carry_forward_hours ??   0) +
    parseFloat(rec?.timeInLieuHours   ?? rec?.time_in_lieu_hours  ??   0);

  function deductionForRequest(r) {
    if (isHalfDayLeave(r)) {
      let hours;
      if (r.hours != null && !isNaN(parseFloat(r.hours))) {
        hours = parseFloat(r.hours);
      } else {
        const period = getHalfDayPeriod(r);
        hours = period === 'PM' ? HALF_DAY_PM_HOURS : HALF_DAY_AM_HOURS;
      }
      return { hours, days: 0.5 };
    }

    if (r.hours != null && !isNaN(parseFloat(r.hours)) && parseFloat(r.hours) > 0) {
      const storedHours = parseFloat(r.hours);
      const storedDays  = r.days != null && !isNaN(parseFloat(r.days))
        ? parseFloat(r.days)
        : storedHours / FULL_DAY_HOURS;
      return { hours: storedHours, days: storedDays };
    }

    let h = 0, d = 0;
    const start = parseISO(r.from);
    const end   = parseISO(r.to);
    const cur   = new Date(start);
    while (cur <= end) {
      if (!isWeekend(cur) && !isNonWorkingDay(empScheds, cur)) {
        h += FULL_DAY_HOURS;
        d += 1;
      }
      cur.setDate(cur.getDate() + 1);
    }
    return { hours: h, days: d };
  }

  function countDeductions(reqs) {
    return reqs.reduce((acc, r) => {
      const { hours, days } = deductionForRequest(r);
      return { hours: acc.hours + hours, days: acc.days + days };
    }, { hours: 0, days: 0 });
  }

  function isDeductible(r) {
    const typeName = r.type ?? r.leaveType?.name;
    if (!typeName) return true;
    const cfg = leaveColorsMap[typeName];
    return cfg ? cfg.affectsBalance !== false : true;
  }

  const empId   = emp.id;
  const empReqs = requests.filter(r => (r.empId ?? r.userId) === empId);
  const used    = countDeductions(empReqs.filter(r => r.status === 'approved' && isDeductible(r)));
  const pending = countDeductions(empReqs.filter(r => r.status === 'pending'  && isDeductible(r)));

  const remainingHours = Math.max(0, totalHours - used.hours);
  const totalDays      = totalHours / defaultHpd;
  const remainingDays  = Math.max(0, totalDays - used.days);

  if (displayUnit === 'hours') {
    const fmtH = h => `${h % 1 === 0 ? h : h.toFixed(1)}h`;
    return {
      remainingLabel: fmtH(remainingHours),
      pendingLabel:   pending.hours > 0 ? fmtH(pending.hours) : null,
      usedHours:      used.hours,
      totalHours,
      remainingHours,
    };
  }

  function fmtDays(days) {
    const whole   = Math.floor(days);
    const hasHalf = Math.abs((days - whole) - 0.5) < 0.01;
    if (hasHalf) return whole > 0 ? `${whole}½d` : '½d';
    return `${Math.round(days)}d`;
  }

  return {
    remainingLabel: fmtDays(remainingDays),
    pendingLabel:   pending.days > 0 ? fmtDays(pending.days) : null,
    usedDays:       used.days,
    totalDays,
    remainingDays,
  };
}